import { useContext } from 'react';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { yupResolver } from '@hookform/resolvers/yup';
import { isFunction, merge } from 'lodash';
import { DEFAULT_FORM_VALUES } from 'modules/tournaments/constants';
import { createBaseTournamentValidationSchema } from 'modules/tournaments/validators';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { GameEntity } from '@okletsplay/core';
import { useDynamicSchemaForTournamentFromGame } from 'modules/games/hooks/use-dynamic-schema-from-game';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { CreateTournamentBaseFormFields } from 'modules/tournaments/interfaces/forms';
import { UserBalanceValidationOptions } from 'modules/payments/validators';
import { GameActivityPaymentContext } from 'modules/game-payments/providers';

interface UseCreateTournamentFormHook {
  form: UseFormReturn<CreateTournamentBaseFormFields>;
}

export const useCreateBaseTournamentForm = (
  game: GameEntity,
  customOptions?: {
    createStaticSchemaFunction?: typeof createBaseTournamentValidationSchema;
    additionalDefaultValues?: Record<string, unknown>;
  }
): UseCreateTournamentFormHook => {
  const allUserPaymentAccounts = useSelector(userPaymentAccountsSelector.all);

  const { paymentConfig, platformFeePercentage } = useContext(GameActivityPaymentContext);

  const dynamicSchema = useDynamicSchemaForTournamentFromGame(game);

  const validationOptions: UserBalanceValidationOptions = {
    getUserAccounts: () => allUserPaymentAccounts,
    getExchangeRate: () => paymentConfig.exchangeRate,
    getPlatformFeePercentage: () => platformFeePercentage,
  };

  const staticSchema =
    customOptions?.createStaticSchemaFunction &&
    isFunction(customOptions?.createStaticSchemaFunction)
      ? customOptions?.createStaticSchemaFunction(validationOptions)
      : createBaseTournamentValidationSchema(validationOptions);

  const defaultValues = {
    ...DEFAULT_FORM_VALUES,
    ...(customOptions?.additionalDefaultValues ?? {}),
  };

  const form = useForm<CreateTournamentBaseFormFields>({
    mode: 'onChange',
    resolver: async (fields, context, options) => {
      const yup = await yupResolver(staticSchema)(fields, context, options);
      const ajv = await ajvResolver(dynamicSchema, { strict: false })(fields, context, options);

      return merge(yup, ajv);
    },
    defaultValues,
  });

  return {
    form,
  };
};
